import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Users, CheckCircle2, Circle, Loader2 } from "lucide-react";

const EXCLUDED = ["moye", "dougherty"];

export default function KeyPersonnelModal({ dealId, onClose, onInserted }) {
  const [people, setPeople] = useState([]);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/contacts").then((r) => {
      const eligible = (r.data || []).filter((p) =>
        p.staffing_pool && p.resume_on_file && !EXCLUDED.some((x) => (p.name || "").toLowerCase().includes(x)));
      setPeople(eligible);
    }).catch(() => {});
  }, []);

  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const insert = async () => {
    if (sel.length === 0) return toast.error("Select at least one person");
    setBusy(true);
    try {
      const r = await api.post(`/bids/${dealId}/key-personnel`, { contact_ids: sel });
      const n = r.data.inserted?.length || 0;
      toast.success(`Drafted ${n} key personnel into the proposal (review before use)`);
      onInserted && onInserted();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Insert failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-xl p-6 space-y-3 shadow-xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="key-personnel-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl text-zinc-900 flex items-center gap-2"><Users className="h-5 w-5 text-gold-dark" /> Key Personnel</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <p className="text-xs text-zinc-500">In-pool people with a resume on file. Selected people are drafted into the proposal’s Key Personnel section for review — never auto-final.</p>

        <div className="space-y-2" data-testid="kp-eligible-list">
          {people.length === 0 && <div className="text-xs text-zinc-400 py-6 text-center border border-dashed border-zinc-300 rounded-lg">No eligible personnel. Mark people as in-pool with a resume in the Data Library › People tab.</div>}
          {people.map((p) => (
            <button key={p.id} data-testid={`kp-person-${p.id}`} onClick={() => toggle(p.id)}
              className={`w-full flex items-center gap-3 text-left border rounded-lg p-3 transition-colors ${sel.includes(p.id) ? "border-gold-dark bg-gold-dark/5" : "border-zinc-200 hover:border-zinc-400"}`}>
              {sel.includes(p.id) ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" /> : <Circle className="h-5 w-5 text-zinc-300 shrink-0" />}
              <div className="min-w-0">
                <div className="text-sm text-zinc-900 font-medium truncate">{p.name}</div>
                <div className="text-[11px] text-zinc-500">{p.title || p.role || "Key Personnel"}</div>
              </div>
            </button>
          ))}
        </div>

        <button onClick={insert} disabled={busy || sel.length === 0} data-testid="kp-insert-btn"
          className="w-full mt-2 bg-zinc-900 text-white font-semibold py-2.5 rounded-md hover:bg-zinc-800 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Drafting…</> : `Draft ${sel.length || ""} into proposal`}
        </button>
      </div>
    </div>
  );
}
